import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, PORTAL_URL, LOGO_URL } from "@/lib/site-data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_24px_-16px_rgba(17,24,39,0.4)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="flex h-[92px] items-center justify-between">
          <a href="#top" data-testid="logo" className="flex items-center gap-2.5 group">
            <img src={LOGO_URL} alt="Xpress N'Go Ship Ltd logo" className="h-[76px] w-auto object-contain" />
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full px-3.5 py-1.5 text-[13px] font-bold text-[#5A189A] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#5A189A] hover:text-white hover:shadow-[0_10px_24px_-8px_rgba(90,24,154,0.7)]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="signin-btn"
              className="text-sm font-medium text-[#1F1F1F] hover:text-[#5A189A] px-4 py-2"
            >
              Sign in
            </a>
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta"
              className="group inline-flex items-center gap-1.5 rounded-full bg-[#E31B23] hover:bg-[#B71319] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(227,27,35,0.6)]"
            >
              Sign Up
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-xl border border-black/10 bg-white text-[#1F1F1F]"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-b border-black/5"
          >
            <div className="px-5 py-5 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 text-base font-bold text-[#5A189A] border-b border-black/5 transition-colors hover:text-[#E31B23]"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-4">
                <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="flex-1 text-center rounded-full border border-black/10 py-3 text-sm font-semibold text-[#1F1F1F]">
                  Sign in
                </a>
                <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="flex-1 text-center rounded-full bg-[#E31B23] py-3 text-sm font-semibold text-white">
                  Sign Up
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
